import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import quotationDefaultsService, { type QuotationDefaults } from '@/services/quotationDefaults.service';

export const QUOTATION_DEFAULTS_QUERY_KEY = ['quotation-defaults'] as const;

export function useQuotationDefaults(enabled = true) {
  return useQuery({
    queryKey: QUOTATION_DEFAULTS_QUERY_KEY,
    queryFn: () => quotationDefaultsService.get(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Cover-letter fields for the browser quotation print (`PrintableQuotation` coverLetter prop). */
export function useQuotationPrintCoverLetter() {
  const { data } = useQuotationDefaults();
  return useMemo(
    () => (data ? { salutation: data.print_salutation, intro: data.print_intro, introRental: data.print_intro_rental } : undefined),
    [data]
  );
}

export function useUpdateQuotationDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<QuotationDefaults>) => quotationDefaultsService.update(payload),
    onSuccess: (data) => queryClient.setQueryData(QUOTATION_DEFAULTS_QUERY_KEY, data),
  });
}

export function useCanManageQuotationDefaults(): boolean {
  const user = useAuthStore((s) => s.user);
  return Boolean(user?.is_super_admin) || (user?.permissions ?? []).includes('crm.quotation_settings.manage');
}
